import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useCrew } from "@/context/CrewProvider";
import { View, StyleSheet, Text } from "react-native";
import Toast from "react-native-toast-message";
import FileInput, { FileProps } from "../file-input";
import { supabase } from "@/lib/supabase";
import { decode } from "base64-arraybuffer";
import { Ionicons } from '@expo/vector-icons';

interface DocumentFormProps {
  onFormSuccess?: () => void;
}

const CREW_BUCKET = 'crew';

export default function DocumentsForm({ onFormSuccess }: DocumentFormProps) {
  const { currentCrewMember } = useCrew();

  async function onFileSelect(file: FileProps, filePath: string) {
    const { error: uploadError } = await supabase.storage
      .from(CREW_BUCKET)
      .upload(filePath, decode(file.base64EncodedFile), { contentType: file.type, upsert: true });

    if (uploadError) {
      Toast.show({
        type: 'error',
        text1: 'Failed to save file.'
      });
    } else {
      Toast.show({
        type: 'success',
        text1: 'File uploaded successfully!'
      });
      if (onFormSuccess) {
        onFormSuccess();
      }
    }
  }

  return (
    <Card style={styles.card}>
      <View style={styles.headerContainer}>
        <View style={styles.iconWrapper}>
          <Ionicons name="document-text" size={18} color="white" />
        </View>
        <Text style={styles.headerTitle}>Documents</Text>
      </View>
      <CardContent style={styles.cardContent}>
        <View style={styles.formContainer}>
          <View style={styles.sectionHeader}>
            <Ionicons name="document" size={16} color="#222" />
            <Text style={styles.sectionTitle}>Passport</Text>
          </View>
          <View style={styles.inputGroup}>
            <View style={styles.formSection}>
              <View style={styles.labelContainer}>
                <Ionicons name="card" size={18} color="#666" />
                <Text style={styles.inputLabel}>Upload your Passport (if applicable)</Text>
              </View>
              <View style={styles.uploadContainer}>
                <FileInput onFileSelect={(file: FileProps | FileProps[]) => {
                  if (!Array.isArray(file)) {
                    onFileSelect(file, `${currentCrewMember?.id}/photos/passport`)
                  }
                }} />
              </View>
            </View>
          </View>

          <View style={styles.sectionHeader}>
            <Ionicons name="car" size={16} color="#222" />
            <Text style={styles.sectionTitle}>Driving License</Text>
          </View>
          <View style={styles.inputGroup}>
            <View style={styles.formSection}>
              <View style={styles.labelContainer}>
                <Ionicons name="card" size={18} color="#666" />
                <Text style={styles.inputLabel}>Upload your Driving License (if applicable)</Text>
              </View>
              <View style={styles.uploadContainer}>
                <FileInput onFileSelect={(file: FileProps | FileProps[]) => {
                  if (!Array.isArray(file)) {
                    onFileSelect(file, `${currentCrewMember?.id}/photos/driving-license`)
                  }
                }} />
              </View>
            </View>
          </View>

          <View style={styles.sectionHeader}>
            <Ionicons name="globe" size={16} color="#222" />
            <Text style={styles.sectionTitle}>Visa</Text>
          </View>
          <View style={styles.inputGroup}>
            <View style={styles.formSection}>
              <View style={styles.labelContainer}>
                <Ionicons name="card" size={18} color="#666" />
                <Text style={styles.inputLabel}>Upload your Visa (if applicable)</Text>
              </View>
              <View style={styles.uploadContainer}>
                <FileInput onFileSelect={(file: FileProps | FileProps[]) => {
                  if (!Array.isArray(file)) {
                    onFileSelect(file, `${currentCrewMember?.id}/visa`)
                  }
                }} />
              </View>
            </View>
          </View>
        </View>
      </CardContent>
    </Card>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 2,
    backgroundColor: '#fff',
    overflow: 'hidden',
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#eee',
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000',
    marginLeft: 8,
  },
  iconWrapper: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#222',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardContent: {
    padding: 0,
  },
  formContainer: {
    padding: 0,
  },
  inputGroup: {
    backgroundColor: '#fff',
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 4,
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    paddingHorizontal: 16,
    marginTop: 5,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#000',
    marginLeft: 6,
  },
  formSection: {
    marginBottom: 12,
  },
  labelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  inputLabel: {
    marginLeft: 6,
    fontSize: 13,
    fontWeight: '500' as const,
    color: '#444',
  },
  uploadContainer: {
    marginVertical: 8,
  }
});